import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

export function RecipeReviewModal({
  scopeJobId,
  ehrId,
  onClose,
}: {
  scopeJobId: string;
  ehrId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState<{ sessionId: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['recipe', scopeJobId],
    queryFn: () => api.getScopeJobRecipe(scopeJobId),
  });

  async function handleAccept() {
    setAccepting(true);
    setError(null);
    try {
      const result = await api.acceptRecipe(scopeJobId);
      setAccepted({ sessionId: result.sessionId });
      void qc.invalidateQueries({ queryKey: ['ehr', ehrId] });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setAccepting(false);
    }
  }

  async function handleReject() {
    setRejecting(true);
    setError(null);
    try {
      await api.rejectRecipe(scopeJobId);
      void qc.invalidateQueries({ queryKey: ['ehr', ehrId] });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRejecting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: 'rgba(0,0,0,0.7)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-4xl max-h-[90vh] flex flex-col rounded-2xl overflow-hidden"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div>
            <div className="text-sm font-semibold text-text-primary">Review Draft Recipe</div>
            <div className="text-xs text-text-dimmed font-mono mt-0.5">{scopeJobId.slice(0, 8)}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-text-dimmed hover:text-text-secondary text-lg leading-none px-2"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {isLoading && (
            <div className="text-sm text-text-dimmed">Loading recipe…</div>
          )}

          {data && (
            <>
              <div className="text-xs text-text-dimmed font-mono">{data.recipePath}</div>

              {/* Preflight status parsed from YAML */}
              <PreflightSummary yaml={data.recipeYaml} />

              <pre
                className="rounded-xl p-4 text-xs font-mono overflow-x-auto leading-relaxed"
                style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >
                {data.recipeYaml}
              </pre>
            </>
          )}

          {accepted && (
            <div
              className="rounded-xl px-4 py-3 text-sm"
              style={{ background: 'var(--accent-green)22', border: '1px solid var(--accent-green)55', color: 'var(--accent-green)' }}
            >
              Recipe accepted. Canary session started: <span className="font-mono">{accepted.sessionId.slice(0, 8)}</span>
            </div>
          )}

          {error && (
            <div
              className="rounded-xl px-4 py-3 text-sm"
              style={{ background: 'var(--accent-red)22', border: '1px solid var(--accent-red)55', color: 'var(--accent-red)' }}
            >
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-border shrink-0">
          {accepted ? (
            <button
              type="button"
              onClick={onClose}
              className="ml-auto rounded-xl px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
            >
              Done
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleReject}
                disabled={rejecting || isLoading}
                className="rounded-xl px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-80 disabled:opacity-40"
                style={{ background: 'var(--accent-red)22', color: 'var(--accent-red)', border: '1px solid var(--accent-red)44' }}
              >
                {rejecting ? 'Discarding…' : 'Discard'}
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-sm text-text-secondary hover:text-text-primary px-3 py-2"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAccept}
                  disabled={accepting || isLoading || !data}
                  className="rounded-xl px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
                  style={{ background: 'linear-gradient(135deg, #01b574 0%, #00c292 100%)' }}
                >
                  {accepting ? 'Starting canary…' : 'Accept & run canary'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PreflightSummary({ yaml: yamlText }: { yaml: string }) {
  const lines = yamlText.split('\n');
  const passedStart = lines.findIndex((l) => l.trim().startsWith('passed:'));
  const failedStart = lines.findIndex((l) => l.trim().startsWith('unresolvedFailures:'));

  const passed: string[] = [];
  const failed: string[] = [];

  if (passedStart >= 0) {
    for (let i = passedStart + 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line || !line.trim().startsWith('- ')) break;
      passed.push(line.trim().replace(/^- /, ''));
    }
  }

  if (failedStart >= 0) {
    for (let i = failedStart + 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line || line.trim() === '') break;
      if (line.trim().startsWith('- endpoint:')) {
        failed.push(line.trim().replace('- endpoint: ', ''));
      }
    }
  }

  if (passed.length === 0 && failed.length === 0) return null;

  return (
    <div className="flex gap-4">
      {passed.length > 0 && (
        <div className="flex-1 rounded-xl p-3" style={{ background: 'var(--accent-green)11', border: '1px solid var(--accent-green)33' }}>
          <div className="text-xs font-semibold mb-1" style={{ color: 'var(--accent-green)' }}>
            Passed ({passed.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {passed.map((p) => (
              <span key={p} className="rounded px-2 py-0.5 text-xs font-mono" style={{ background: 'var(--accent-green)22', color: 'var(--accent-green)' }}>{p}</span>
            ))}
          </div>
        </div>
      )}
      {failed.length > 0 && (
        <div className="flex-1 rounded-xl p-3" style={{ background: 'var(--accent-yellow)11', border: '1px solid var(--accent-yellow)33' }}>
          <div className="text-xs font-semibold mb-1" style={{ color: 'var(--accent-yellow)' }}>
            Needs fixing ({failed.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {failed.map((f) => (
              <span key={f} className="rounded px-2 py-0.5 text-xs font-mono" style={{ background: 'var(--accent-yellow)22', color: 'var(--accent-yellow)' }}>{f}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
